import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  Message,
  ToolCallInfo,
  type AlarmRecommendation,
  type InvestigationPath,
  type StructuredAnswer,
} from '@/ai/utils/types';
import { AnimationItem } from 'lottie-web';
import lottie from 'lottie-web/build/player/lottie_light';
import { marked } from 'marked';
import animationData from '@/common/assets/ai/window-bot.json';
import { TOOL_LABELS, toolRole } from '@/ai/utils/toolMeta';
import {
  parseReportPayload,
  type ChartSection,
  type ReportPayload,
} from '@/analysis/utils/reportSchema';
import { useChatbotConfigStore } from '@/config/stores/chatbotConfig';
import { useInternational } from '@/common/locale';
import { formatMessageTime, formatMessageTimeFull } from '@/ai/utils/utils';

// GFM + breaks (단일 줄바꿈을 <br> 로) 옵션. Gemini 가 보내는 헤더/리스트/볼드/코드블록을
// 깔끔하게 HTML 로 변환하도록 강제. v-dompurify-html 이 sanitize 해주므로 안전.
marked.setOptions({
  gfm: true,
  breaks: true,
});

export interface Props {
  message: Message;
}

export interface Emit {
  (e: 'message-click', msg: string): void;
  /** Suggestion pill 클릭 — ChatWindow 가 입력창에 채움. */
  (e: 'suggestion-click', text: string): void;
}

/**
 * bot 답변 끝의 `[SUGGESTIONS]\n- ...\n[/SUGGESTIONS]` 블록을 본문에서 분리.
 * - 닫는 태그까지 모두 도착한 경우: 본문에서 strip + suggestions 배열 반환
 * - 여는 태그만 있고 닫는 태그 미수신:
 *   · streaming 중 (allowUnclosed=false): 여는 태그부터 끝까지 잘라내고 suggestions=[]
 *     (partial 노출 회피)
 *   · 완성 메시지 (allowUnclosed=true): 작은 모델이 닫는 태그를 누락하는 케이스 구제 —
 *     여는 태그 이후의 bullet 들을 suggestions 로 채택
 * - 두 태그 모두 없음: 원본 그대로
 */
const SUGGESTIONS_FULL_RE = /\n*\[SUGGESTIONS\]\s*\n([\s\S]*?)\n\[\/SUGGESTIONS\]\s*$/;
const MAX_SUGGESTIONS = 5;

const toSuggestionItems = (block: string): string[] =>
  block
    .split('\n')
    .map((l) => l.trim().replace(/^[-*•]\s*/, ''))
    .filter((l) => l.length > 0 && l !== '[/SUGGESTIONS]')
    .slice(0, MAX_SUGGESTIONS);

export const parseSuggestionsAndBody = (
  raw: string,
  opts?: { allowUnclosed?: boolean },
): { body: string; suggestions: string[] } => {
  if (!raw) return { body: '', suggestions: [] };
  const m = raw.match(SUGGESTIONS_FULL_RE);
  if (m) {
    return {
      body: raw.replace(SUGGESTIONS_FULL_RE, '').trimEnd(),
      suggestions: toSuggestionItems(m[1]),
    };
  }
  const idx = raw.indexOf('[SUGGESTIONS]');
  if (idx >= 0) {
    const body = raw.slice(0, idx).trimEnd();
    if (opts?.allowUnclosed) {
      return { body, suggestions: toSuggestionItems(raw.slice(idx + '[SUGGESTIONS]'.length)) };
    }
    return { body, suggestions: [] };
  }
  return { body: raw, suggestions: [] };
};

/** alarmRecsJson 파싱 결과를 좁히는 타입 가드 (No `as` 단언 규칙 준수 — `in` 내로잉). */
const isAlarmRecommendation = (x: unknown): x is AlarmRecommendation => {
  if (typeof x !== 'object' || x === null || !('metrics' in x)) return false;
  const { metrics } = x;
  return Array.isArray(metrics) && metrics.length > 0;
};

/** structuredAnswerJson 파싱 결과를 좁히는 타입 가드 (No `as` 단언 규칙 준수 — `in` 내로잉). */
const isStructuredAnswer = (x: unknown): x is StructuredAnswer => {
  if (typeof x !== 'object' || x === null || !('verdict' in x)) return false;
  const { verdict } = x;
  return typeof verdict === 'string';
};

/** investigation result 파싱 결과를 좁히는 타입 가드 (No `as` 단언 규칙 준수 — `in` 내로잉). */
const isInvestigationPath = (x: unknown): x is InvestigationPath => {
  if (typeof x !== 'object' || x === null || !('source' in x) || !('related' in x)) return false;
  const { source, related } = x;
  return typeof source === 'object' && source !== null && Array.isArray(related);
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();

  const container = ref<HTMLDivElement | null>(null);
  let loadingScreenAnimation: AnimationItem | null = null;

  const parsedResult = computed(() => {
    try {
      return JSON.parse(props.message.result);
    } catch {
      return [];
    }
  });

  /**
   * marked 가 만든 anchor 중 `href="target://..."` 는 DOMPurify 의 안전한 scheme 화이트리스트에
   * 포함되지 않아 sanitize 단계에서 href 가 스트립된다. 클릭 시 가로채 라우팅하기 위해
   * 후처리로 `href="#"` 로 바꾸고 원래 path 는 `data-target` 속성에 보관 — BotMessage 가
   * `data-target` 을 읽어 dispatchTargetDetailFromBot 호출.
   */
  const rewriteTargetAnchors = (html: string): string =>
    html.replace(
      /<a([^>]*?)href="target:\/\/([^"]+)"([^>]*)>/gi,
      (_m, before, path, after) => `<a${before}href="#" data-target="${escapeAttr(path)}"${after}>`,
    );

  const escapeAttr = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  /** SUGGESTIONS 블록과 본문을 분리한 결과 — 완성 메시지는 닫는 태그 누락도 구제. */
  const parsed = computed(() =>
    parseSuggestionsAndBody(props.message.message ?? '', {
      allowUnclosed: !props.message.isPartial,
    }),
  );

  const parsedMessage = computed(() => {
    if (!parsed.value.body) return '';
    return rewriteTargetAnchors(marked(parsed.value.body) as string);
  });

  /** bot 답변 끝의 추천 질문 pill 텍스트 배열. */
  const suggestions = computed<string[]>(() => parsed.value.suggestions);

  /** Suggestion pill 클릭 → ChatWindow 가 입력창에 채워주도록 emit. */
  const handleSuggestionClick = (text: string) => {
    emit('suggestion-click', text);
  };

  /** 칩 latency 표기 — 1초 이상은 "1.5s" 로 축약 (ms 나열보다 읽기 쉬움). */
  const formatLatency = (ms: number): string =>
    ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

  /** 스트리밍 중 노출할 도구 호출 진행 chip 목록. */
  const toolCallChips = computed<
    Array<{
      key: string;
      label: string;
      icon: string;
      status: ToolCallInfo['status'];
      latencyText?: string;
    }>
  >(() => {
    const calls = props.message.toolCalls ?? [];
    return calls.map((tc, idx) => {
      const icon = TOOL_LABELS[tc.name]?.icon ?? 'ev-icon-cog';
      return {
        key: `${tc.name}-${idx}`,
        label: toolRole(tc.name) || tc.name,
        icon,
        status: tc.status,
        latencyText: tc.latencyMs != null ? formatLatency(tc.latencyMs) : undefined,
      };
    });
  });

  const isPartial = computed(() => !!props.message.isPartial);

  /**
   * Lightweight overall 진행 indicator — 풍선 상단 1줄. partial 일 때만 표시.
   * statusKind === 'thinking' → MESSAGE.THINKING
   * statusKind === 'tool' + statusToolName → toolRole() 로 라벨 변환 → MESSAGE.TOOL_RUNNING
   * (매핑에 없으면 fallback MESSAGE.TOOL_CALLING)
   */
  const statusText = computed<string | null>(() => {
    if (!props.message.isPartial) return null;
    const kind = props.message.statusKind;
    if (kind === 'thinking') return t('WORD.CHATBOT.MESSAGE.THINKING');
    if (kind === 'tool') {
      const name = props.message.statusToolName;
      if (!name) return t('WORD.CHATBOT.MESSAGE.TOOL_CALLING');
      const label = toolRole(name);
      return label
        ? t('WORD.CHATBOT.MESSAGE.TOOL_RUNNING', { tool: label })
        : t('WORD.CHATBOT.MESSAGE.TOOL_CALLING');
    }
    return null;
  });

  /**
   * generate_report 도구가 만든 메시지 (resultType==='report') 의 ReportPayload.
   * BotMessage 가 카드 형태로 "자세히 보기" 버튼을 렌더할 때 사용.
   * 파싱 실패 시 null — BotMessage 분기 자체가 미렌더되어 graceful fallback.
   */
  const parsedReport = computed<ReportPayload | null>(() => {
    if (props.message.resultType !== 'report') return null;
    return parseReportPayload(props.message.result);
  });

  /**
   * 인라인 차트 — 백엔드가 추이/Top-N 도구 결과에서 자동 첨부한 chart 섹션 배열
   * (리포트 스키마 동일 → ChartSection.vue 재사용). 파싱 실패/형식 불일치는 조용히 미렌더.
   */
  const inlineCharts = computed<ChartSection[]>(() => {
    if (!props.message.chartsJson) return [];
    try {
      const parsed = JSON.parse(props.message.chartsJson);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((c) => c && c.type === 'chart' && Array.isArray(c.series) && c.series.length > 0)
        .slice(0, 4) as ChartSection[];
    } catch {
      return [];
    }
  });

  /**
   * 알람 임계치 추천 — 백엔드 recommend_alarm_thresholds 결과(인스턴스별 배열).
   * AlarmRecommendTable 이 표 + 행별 '알람 추가' 버튼으로 렌더. 파싱 실패/형식 불일치는 조용히 미렌더.
   */
  const alarmRecs = computed<AlarmRecommendation[]>(() => {
    if (!props.message.alarmRecsJson) return [];
    try {
      const parsed: unknown = JSON.parse(props.message.alarmRecsJson);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isAlarmRecommendation);
    } catch {
      return [];
    }
  });

  /**
   * 구조화 응답 카드 — present_structured_answer 결과(단일 객체). StructuredAnswer.vue 가 고정
   * 레이아웃(verdict/요약표/원인·근거/조치)으로 렌더. verdict 없으면 미렌더.
   */
  const structuredCard = computed<StructuredAnswer | null>(() => {
    if (!props.message.structuredAnswerJson) return null;
    try {
      const parsed: unknown = JSON.parse(props.message.structuredAnswerJson);
      return isStructuredAnswer(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });

  /**
   * 연계 조사 카드 — 챗봇이 분석을 연관 타겟까지 확장 조사했을 때 (resultType 'investigation')
   * 배너 + 추적 경로 체인 + 연계 분석 액션을 InvestigationPathCard 가 렌더.
   * 파싱 실패/형식 불일치 시 null — 분기 자체 미렌더로 graceful fallback.
   */
  const investigationCard = computed<InvestigationPath | null>(() => {
    if (props.message.resultType !== 'investigation') return null;
    try {
      const parsed: unknown = JSON.parse(props.message.result);
      return isInvestigationPath(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });

  /** 소요시간 ms → "X분 Y초" (1분 미만은 "Y초"). */
  const formatElapsed = (ms: number): string => {
    const totalSec = Math.max(0, Math.round(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return m > 0
      ? t('WORD.CHATBOT.MESSAGE.ELAPSED_MIN_SEC', { m, s })
      : t('WORD.CHATBOT.MESSAGE.ELAPSED_SEC', { s });
  };

  const chatbotConfig = useChatbotConfigStore();

  /**
   * 응답 메타 라인 — "모델 · 토큰 N (입력 i · 출력 o) · X분 Y초".
   * 항목별 표시 여부는 환경설정 responseMetaOptions (기본 모두 on) 를 따른다 —
   * 메시지에는 전체 메타가 저장되므로 토글 변경이 과거 메시지에도 소급 적용.
   * 토큰 미보고 (total 0) 시 토큰 항목은 생략.
   */
  const responseMetaLine = computed<string | null>(() => {
    if (props.message.isPartial || !props.message.metaJson) return null;
    let meta: {
      provider?: string;
      model?: string;
      inputTokens?: number;
      outputTokens?: number;
      totalTokens?: number;
      elapsedMs?: number;
    };
    try {
      meta = JSON.parse(props.message.metaJson);
    } catch {
      return null;
    }
    const opts = chatbotConfig.settings?.responseMetaOptions ?? {};
    const show = (k: string) => opts[k] !== false; // 미지정 키는 기본 on
    const parts: string[] = [];
    if (show('showModel') && (meta.model || meta.provider)) {
      parts.push(meta.model || meta.provider || '');
    }
    if (show('showTokens') && typeof meta.totalTokens === 'number' && meta.totalTokens > 0) {
      parts.push(
        t('WORD.CHATBOT.MESSAGE.TOKENS_META', {
          total: meta.totalTokens.toLocaleString(),
          input: (meta.inputTokens ?? 0).toLocaleString(),
          output: (meta.outputTokens ?? 0).toLocaleString(),
        }),
      );
    }
    if (show('showElapsed') && typeof meta.elapsedMs === 'number') {
      parts.push(formatElapsed(meta.elapsedMs));
    }
    return parts.length ? parts.join(' · ') : null;
  });

  /** 답변 받은 시각 (HH:mm) + 툴팁(전체 일시). welcome/partial 은 BotMessage 템플릿에서 미표시. */
  const messageTime = computed(() => formatMessageTime(props.message.createdAt));
  const messageTimeFull = computed(() => formatMessageTimeFull(props.message.createdAt));

  const handleClick = (msg: string) => {
    emit('message-click', msg);
  };

  onMounted(() => {
    if (container.value) {
      loadingScreenAnimation = lottie.loadAnimation({
        container: container.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      });
    }
    // 응답 메타 표시 옵션 (responseMetaOptions) 용 설정 1회 로드 — 이미 있으면/로딩 중이면 skip.
    if (!chatbotConfig.settings && !chatbotConfig.settingsLoading) {
      chatbotConfig.fetchSettings();
    }
  });

  onUnmounted(() => {
    if (loadingScreenAnimation) {
      loadingScreenAnimation.destroy();
    }
  });

  return {
    parsedResult,
    parsedMessage,
    parsedReport,
    inlineCharts,
    alarmRecs,
    structuredCard,
    investigationCard,
    suggestions,
    toolCallChips,
    isPartial,
    statusText,
    responseMetaLine,
    messageTime,
    messageTimeFull,
    handleClick,
    handleSuggestionClick,
    container,
  };
};
